import { useVenue } from '@core/contexts/VenueContext';
import { useNotifications } from '@core/hooks/useNotifications';
import { brandLogoUrl } from '@core/hooks/useBranding';
import { BrandLogo } from '@core/components/BrandLogo';
import { ArrowLeft, Bell } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderProps {
  title?: string;
  showBack?: boolean;
  showNotifications?: boolean;
}

export function Header({ title, showBack = false, showNotifications = true }: HeaderProps) {
  const { venue } = useVenue();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useNotifications();

  // Проверяем pathname - главная страница это "/" или "/?venue=..."
  const isHomePage = location.pathname === '/' || location.pathname === '';
  const isNotificationsPage = location.pathname === '/notifications';
  const displayTitle = title || venue?.name || 'Lunit Loyalty';

  // Автоматически показываем кнопку "Назад" если не главная страница
  // Если showBack явно передан true, показываем всегда
  const shouldShowBack = showBack === true || !isHomePage;

  const handleBack = () => {
    navigate('/');
  };

  const handleNotifications = () => {
    navigate('/notifications');
  };

  return (
    <header className="sticky top-0 z-10 bg-page/80 backdrop-blur-lg border-b border-border">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Кнопка назад или пустое место для симметрии */}
        <div className="w-10">
          {shouldShowBack && (
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-full hover:bg-card transition-colors"
              aria-label="Назад на главную"
            >
              <ArrowLeft className="w-6 h-6 text-primary" />
            </button>
          )}
        </div>

        {/* Логотип и название */}
        <div className="flex-1 flex items-center justify-center gap-2">
          <BrandLogo
            src={brandLogoUrl(venue?.branding)}
            alt={venue?.name ?? ''}
            className="h-8 w-8 shrink-0 rounded-lg"
          />
          <h1 className="text-lg font-bold text-center truncate max-w-[200px] text-text-main">
            {displayTitle}
          </h1>
        </div>

        {/* Колокольчик уведомлений */}
        <div className="w-10 flex justify-end">
          {showNotifications && !isNotificationsPage && (
            <button
              onClick={handleNotifications}
              className="relative p-2 -mr-2 rounded-full hover:bg-card transition-colors"
              aria-label="Уведомления"
            >
              <Bell className="w-6 h-6 text-primary" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
